// OrdersApi.tsx
import axios from 'axios';
import apiConfig from '../apiConfig.ts';

const API_BASE_URL = apiConfig.apiBaseURL;

// Order interfaces
export interface Order {
  _id: string;
  orderId: string;
  orderDate: string;
  userName: string;
  recipient: string | null;
  occasion: string;
  milestone: string;
  giftType: string;
  giftStatus: string;
  deliveryDate: string | null;
  giftDetails: any;
  recurring: string;
}

// API Response interfaces
export interface OrderResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: Order[];
  pagination?: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
  };
}

export interface SingleOrderResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: any; // Full order details
}

export interface OrderCountsResponse {
  status: 'Success' | 'Failed';
  message: string;
  giftCounts?: {
    totalGifts: number;
    upcomingGifts: number;
    processingGifts: number;
    shippedGifts: number;
    deliveredGifts: number;
  };
}

// Parameters for API calls
export interface GetOrdersParams {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
  giftStatus?: string;
  giftType?: string;
  startDate?: string;
  endDate?: string;
}

export interface UpdateVerificationRequest {
  milestoneConfirmed?: boolean;
  deathConfirmed?: boolean;
}

export interface UpdateStatusRequest {
  giftStatus: 'Upcoming' | 'Processing' | 'Shipped' | 'Delivered';
}

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

export const ordersApi = {
  // Get all orders with pagination, search, and filtering
  getAllOrders: async (params: GetOrdersParams = {}): Promise<OrderResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.giftStatus) queryParams.append('giftStatus', params.giftStatus);
      if (params.giftType) queryParams.append('giftType', params.giftType);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const response = await axios.get(
        `${API_BASE_URL}/order/get-all-orders-list?${queryParams.toString()}`,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch orders',
        data: [],
        pagination: {
          totalCount: 0,
          totalPages: 0,
          currentPage: 1,
        },
      };
    }
  },

  // Get all unverified conditional gifts
  getAllUnverifiedConditionalGifts: async (params: GetOrdersParams = {}): Promise<OrderResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.giftStatus) queryParams.append('giftStatus', params.giftStatus);
      if (params.giftType) queryParams.append('giftType', params.giftType);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const response = await axios.get(
        `${API_BASE_URL}/order/get-all-unverified-conditional-gifts?${queryParams.toString()}`,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching unverified conditional gifts:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch unverified conditional gifts',
        data: [],
        pagination: {
          totalCount: 0,
          totalPages: 0,
          currentPage: 1,
        },
      };
    }
  },

  // Get all unverified post-mortem gifts
  getAllUnverifiedPostMortemGifts: async (params: GetOrdersParams = {}): Promise<OrderResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.giftStatus) queryParams.append('giftStatus', params.giftStatus);
      if (params.giftType) queryParams.append('giftType', params.giftType);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const response = await axios.get(
        `${API_BASE_URL}/order/get-all-unverified-post-mortem-gifts?${queryParams.toString()}`,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching unverified post-mortem gifts:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch unverified post-mortem gifts',
        data: [],
        pagination: {
          totalCount: 0,
          totalPages: 0,
          currentPage: 1,
        },
      };
    }
  },

  // Get single order by ID
  getSingleOrder: async (giftId: string): Promise<SingleOrderResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/order/get-single-order/${giftId}`,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch order details');
    }
  },

  // Update gift verification status
  updateGiftVerification: async (giftId: string, verificationData: UpdateVerificationRequest): Promise<SingleOrderResponse> => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/order/update-gift-verification/${giftId}`,
        verificationData,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update verification status');
    }
  },

  // Update gift status
  updateGiftStatus: async (giftId: string, statusData: UpdateStatusRequest): Promise<SingleOrderResponse> => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/order/update-gift-status/${giftId}`,
        statusData,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update gift status');
    }
  },

  // Download audio for a gift
  downloadAudio: async (giftId: string): Promise<void> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/order/download-audio/${giftId}`,
        {
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );

      // Check if response is an error JSON masquerading as blob
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.message || 'Failed to download audio');
      }

      const blob = new Blob([response.data], { type: contentType || 'audio/mpeg' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audio-${giftId}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.message || 'Failed to download audio');
        } catch (parseError) {
          if (parseError instanceof SyntaxError) {
            throw new Error('Failed to download audio');
          }
          throw parseError;
        }
      }
      throw new Error(error.response?.data?.message || error.message || 'Failed to download audio');
    }
  },

  // Get order counts/stats
  getOrderCounts: async (): Promise<OrderCountsResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/order/get-order-counts`,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch order counts');
    }
  },
};